import { addMinutes } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Activity,
  Gauge,
  Hash,
  Sigma,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import React, { useMemo } from "react";
import { getDayDates } from "@globals/daily";
import { formatDate } from "@globals/format";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { useStatsHistory } from "@client/hooks/useStatsHistory";
import { useToday } from "@client/hooks/useToday";
import { fix, number, unit, unundefined } from "@client/utils/values";

const formatDayDate = (date: Date): string => {
  const { start: startToday, end: endToday } = getDayDates(new Date());
  const { start, end } = getDayDates(date);
  if (start >= startToday && end <= endToday) return "Dnes";

  const { start: startYesterday, end: endYesterday } = getDayDates(
    new Date(new Date().setDate(new Date().getDate() - 1))
  );
  if (start >= startYesterday && end <= endYesterday) return "Včera";

  const { start: startYesterday2, end: endYesterday2 } = getDayDates(
    new Date(new Date().setDate(new Date().getDate() - 2))
  );
  if (start >= startYesterday2 && end <= endYesterday2) return "Předvčírem";

  return formatDate(date, "d. M.");
};

function formatTime(date: Date) {
  return formatDate(addMinutes(date, date.getTimezoneOffset()), "HH:mm:ss");
}

type DayHistoryProps = {
  stats: dailyStats;
  valueF?: (val: string) => string;
};

const DayHistory: React.FC<DayHistoryProps> = React.memo(({ stats, valueF = (val: string) => val }) => {
  const isBooleanStats = stats.uptime !== null || stats.downtime !== null;

  return (
    <div className="p-2 rounded bg-card flex flex-col h-full grow min-w-max shrink-0">
      <h3 className="text-lg font-semibold mb-2">{formatDayDate(stats.date)}</h3>
      {isBooleanStats ? (
        <div className="space-y-2 text-sm">
          <div className="flex gap-1.5 whitespace-nowrap items-center">
            <ArrowUpDown className="h-4 w-4 text-status-warning" />
            <span className="text-muted-foreground">Počet:</span>
            <span className="font-medium">{stats.risingCount}</span>
          </div>
          <div className="flex gap-1.5 whitespace-nowrap items-center">
            <ArrowUp className="h-4 w-4 text-status-ok" />
            <span className="text-muted-foreground">1:</span>
            <span className="font-medium">
              {formatTime(new Date(Math.round((stats.uptime as number) / 1000) * 1000))}
            </span>
          </div>
          <div className="flex gap-1.5 whitespace-nowrap items-center">
            <ArrowDown className="h-4 w-4 text-status-serious" />
            <span className="text-muted-foreground">0:</span>
            <span className="font-medium">
              {formatTime(new Date(Math.round((stats.downtime as number) / 1000) * 1000))}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-status-warning" />
              <span className="font-semibold text-2xl leading-none">{valueF(String(stats.avg))}</span>
            </div>
            <div className="flex flex-col items-end gap-1 ml-4">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-status-serious" />
                <span className="font-medium text-status-serious">{valueF(String(stats.max))}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingDown className="h-4 w-4 text-status-info" />
                <span className="font-medium text-status-info">{valueF(String(stats.min))}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-1.5">
            <Hash className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Počet:</span>
            <span className="font-medium">{stats.count}</span>
          </div>
          {stats.median != null && (
            <div className="flex items-center space-x-1.5">
              <Sigma className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Medián:</span>
              <span className="font-medium">{valueF(String(stats.median))}</span>
            </div>
          )}
          {stats.stddev != null && (
            <div className="flex items-center space-x-1.5">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">σ:</span>
              <span className="font-medium">{valueF(String(stats.stddev))}</span>
            </div>
          )}
          {stats.p05 != null && stats.p95 != null && (
            <div className="flex items-center space-x-1.5">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">p05–p95:</span>
              <span className="font-medium">
                {valueF(String(stats.p05))}–{valueF(String(stats.p95))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

type DailyHistoryProps = {
  topicId: string;
  boolean?: boolean;
};

// TODO: cursored "pagination" - load more on scroll
const DailyHistory: React.FC<DailyHistoryProps> = ({ boolean = false, topicId }) => {
  // useMetricMeta místo useTopic — metadata se nemění s každým WS valueum, žádný
  // zbytečný re-render na value updates.
  const topic = useMetricMeta(topicId);
  const { stats: todayStats } = useToday({ topicId });
  const { stats: fetched } = useStatsHistory(topicId);

  const history = useMemo(() => {
    if (todayStats && fetched.length > 0) return [todayStats, ...fetched];
    if (todayStats) return [todayStats];
    return fetched;
  }, [todayStats, fetched]);

  return (
    <div className="overflow-x-auto flex m-1.5 [scrollbar-width:thin] [scrollbar-color:rgb(163_163_163)_rgb(229_229_229)] dark:[scrollbar-color:rgb(82_82_82)_rgb(38_38_38)]">
      <div className="flex gap-2 m-0.5">
        {history.map((stat, index) => (
          <DayHistory
            key={index}
            stats={stat}
            valueF={
              boolean
                ? (v) => unundefined(v, "---")
                : (v) => unit(unundefined(fix(number(v), topic?.decimals ?? 1), "---"), topic?.unit ?? "")
            }
          />
        ))}
      </div>
    </div>
  );
};

export default React.memo(DailyHistory);
